import { Component, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { debounceTime, Observable, of, startWith, Subscription } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslateModule } from '@ngx-translate/core';
import { InputSelect } from '../../models/pages-model/form-general.model';

@Component({
  selector: 'app-input-general-autocomplete',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    TranslateModule,
  ],
  templateUrl: './input-general-autocomplete.component.html',
})
export class InputGeneralAutocompleteComponent {
  @Input() formControlGeneral: FormControl | AbstractControl | null = new FormControl();
  @Input() formControlGeneralValue: FormControl | AbstractControl | null = new FormControl();
  @Input() placeholder = '';
  @Input() label = '';
  @Input() inputSelect: InputSelect = { value: '', description: '' };
  @Input() arrayData: any[] | null = [];
  public localArrayData: any = [];
  public subscribeArray: Subscription[] = [];
  public filteredOptions!: Observable<any[]>;

  ngOnInit() {
    this.setupAutocomplete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['arrayData']) {
      this.subscribeArray.forEach((sub) => sub.unsubscribe());
      this.subscribeArray = [];
      this.setupAutocomplete();
    }
  }

  ngOnDestroy() {
    this.subscribeArray.forEach((sub) => sub.unsubscribe());
  }

  private setupAutocomplete(): void {
    this.localArrayData = [...(this.arrayData ?? [])];
    this.formControlGeneral?.clearValidators();
    this.formControlGeneral?.addValidators([
      Validators.required,
      this.optionMatchValidator(this.localArrayData, this.inputSelect.description),
    ]);
    this.formControlGeneral?.updateValueAndValidity({ emitEvent: false });
    const sub = this.formControlGeneral?.valueChanges.pipe(startWith('')).subscribe(() => {
      if (
        this.formControlGeneral?.valid &&
        this.formControlGeneral?.value !== null &&
        this.formControlGeneral?.value !== undefined
      ) {
        const selectedOption = this.arrayData?.find((option) =>
          this.inputSelect.description
            ? this.getNestedProperty(option, this.inputSelect.description) ===
              this.formControlGeneral?.value
            : option === this.formControlGeneral?.value,
        );
        const selectedValue = selectedOption
          ? this.getNestedProperty(selectedOption, this.inputSelect.value)
          : null;

        if (this.formControlGeneralValue?.value !== selectedValue) {
          this.formControlGeneralValue?.setValue(selectedValue);
        }
      }
      this.filteredOptions = this._filterAsync((this.formControlGeneral?.value ?? null) as string);
    });

    if (sub) {
      this.subscribeArray.push(sub);
    }
  }

  private _filterAsync(value: string): Observable<any[]> {
    if (!value) return of(this.localArrayData);
    const filterValue = value.toLowerCase();

    return of(
      this.localArrayData.filter((option: any) => {
        const displayValue = this.inputSelect.description
          ? String(this.getNestedProperty(option, this.inputSelect.description)).toLowerCase()
          : String(option).toLowerCase();

        return displayValue.includes(filterValue);
      }),
    );
  }

  public getNestedProperty(obj: any, path: string): any {
    if (!obj || !path) return null; // Validar entradas
    const pathArray = path.split('.');
    return pathArray.reduce((acc, current) => (acc ? acc[current] : null), obj) || ''; // Devolver una cadena vacía si no existe
  }

  private optionMatchValidator(localArrayData: any[], dataPath?: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // Si el valor es vacío, no hay error.
      }
      const isValid = localArrayData.some((option) =>
        dataPath
          ? this.getNestedProperty(option, dataPath) === control.value
          : option === control.value,
      );
      return isValid ? null : { optionMismatch: true };
    };
  }

  get getInputGeneral(): FormControl {
    return this.formControlGeneral as FormControl;
  }

  get hasErrorState(): boolean {
    return (
      !!this.formControlGeneral &&
      this.formControlGeneral.invalid &&
      (this.formControlGeneral.touched || this.formControlGeneral.dirty)
    );
  }

  isRequired(control: AbstractControl | null): boolean {
    return !!control?.hasValidator?.(Validators.required);
  }
}
